// Activity rail registry — local pushes from any page + a poller against /api/audit-events.
import { useCallback, useEffect, useState } from 'react';
import { api } from './apiClient.js';

const listeners = new Set();
const localEntries = [];
let remoteEntries = [];
let pollerStarted = false;
let pollerId = null;
let railOpen = false;
const openListeners = new Set();

function snapshot() {
  // Local entries appear first (fresh), then the remote audit log.
  return [...localEntries, ...remoteEntries].slice(0, 80);
}

function notify() {
  const snap = snapshot();
  for (const cb of listeners) cb(snap);
}

function notifyOpen() {
  for (const cb of openListeners) cb(railOpen);
}

export const activityRail = {
  push(entry) {
    const e = {
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      fresh: true,
      occurredAt: new Date().toISOString(),
      ...entry,
    };
    localEntries.unshift(e);
    if (localEntries.length > 24) localEntries.pop();
    notify();
    setTimeout(() => {
      e.fresh = false;
      notify();
    }, 4500);
  },
  open() {
    railOpen = true;
    notifyOpen();
  },
  close() {
    railOpen = false;
    notifyOpen();
  },
  toggle() {
    railOpen = !railOpen;
    notifyOpen();
  },
  isOpen() {
    return railOpen;
  },
};

async function pollRemote() {
  try {
    const res = await api.get('/audit-events', { query: { limit: 30 } });
    const data = res?.data || [];
    remoteEntries = data.map((evt) => ({
      id: evt.id,
      action: evt.action,
      targetType: evt.targetType,
      targetId: evt.targetId,
      actorUserId: evt.actorUserId,
      occurredAt: evt.occurredAt,
      summary: evt.payload?.summary || null,
      fresh: false,
    }));
    notify();
  } catch {
    // ignore — UI keeps last known.
  }
}

function startPoller() {
  if (pollerStarted) return;
  pollerStarted = true;
  pollRemote();
  pollerId = setInterval(pollRemote, 10000);
}

function stopPoller() {
  if (pollerId) clearInterval(pollerId);
  pollerId = null;
  pollerStarted = false;
  remoteEntries = [];
  notify();
}

export function useActivityRail(enabled = true) {
  const [entries, setEntries] = useState(snapshot());
  const [open, setOpen] = useState(railOpen);
  useEffect(() => {
    listeners.add(setEntries);
    openListeners.add(setOpen);
    setEntries(snapshot());
    setOpen(railOpen);
    if (enabled) {
      startPoller();
    } else {
      stopPoller();
    }
    return () => {
      listeners.delete(setEntries);
      openListeners.delete(setOpen);
    };
  }, [enabled]);
  return {
    entries,
    open,
    toggle: useCallback(() => activityRail.toggle(), []),
    show: useCallback(() => activityRail.open(), []),
    hide: useCallback(() => activityRail.close(), []),
    push: activityRail.push,
    refreshRemote: pollRemote,
  };
}
